import { Paperclip, PenLine, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import type {
  QuoteRequestAttachmentUpload,
  QuoteRequestIntakeDetail,
} from "@/services/quoteRequestsService";

export interface QuoteRequestSummaryStepProps {
  title: string;
  description: string;
  attachments: QuoteRequestAttachmentUpload[];
  /**
   * Values the consumer typed into the intake's text items. Listed here for
   * the same reason the attachments are: this screen answers "what am I about
   * to send", and an answer that goes out unseen is not answered.
   */
  intakeDetails?: QuoteRequestIntakeDetail[];
  /** Professions this will be sent to — two when the job spans both. */
  disciplines?: Array<"notary" | "accountant">;
}

/**
 * The last screen before the request is created.
 *
 * It used to be a label/value table — "Título de la solicitud" in one column,
 * the text in a muted second column — which made the description, the single
 * thing professionals price against, read like a table cell. It is prose now,
 * for the same reason the review step is.
 *
 * Its job is deliberately NOT the review step's. The review asks "are these
 * the right words"; this one answers "what happens when I press the button" —
 * who it reaches, how many quotes come back, and that nothing is charged yet.
 * Those were the questions the old screen left entirely unanswered, and a
 * consumer about to commit is entitled to them.
 *
 * Named with a `Step` suffix to avoid colliding with the `QuoteRequestSummary`
 * DTO exported from quoteRequestsService.ts.
 */
export function QuoteRequestSummaryStep({
  title,
  description,
  attachments,
  intakeDetails = [],
  disciplines = [],
}: QuoteRequestSummaryStepProps): JSX.Element {
  const { t } = useTranslation();
  // The PLURAL, lowercase form — these go into running prose ("los contadores
  // y escribanos que pueden hacerlo"), not onto a badge. quoteDiscipline_* is
  // the title-case label and reads as broken Spanish inside a sentence.
  const disciplineNames = disciplines.map((d) => t(`requests.user.quoteDisciplinePlural_${d}`));

  // Each step leads with the action in the consumer's own terms, then explains
  // it. The list was three equal-weight muted sentences, which has to be read
  // in full to extract three facts that a glance should have given up.
  const steps: Array<{ lead: string; body: string }> = [
    {
      lead: t("requests.user.quoteSummaryNextBroadcastLead"),
      body:
        disciplineNames.length > 0
          ? t("requests.user.quoteSummaryNextBroadcastKnown", {
              disciplines: disciplineNames.join(t("requests.user.quoteDisciplineJoin")),
            })
          : t("requests.user.quoteSummaryNextBroadcast"),
    },
    {
      lead: t("requests.user.quoteSummaryNextQuotesLead"),
      // Plural matters: a two-discipline request returns two separate quotes,
      // awarded and paid one at a time.
      body: t(
        disciplineNames.length > 1
          ? "requests.user.quoteSummaryNextQuotesTwo"
          : "requests.user.quoteSummaryNextQuotes",
      ),
    },
    {
      lead: t("requests.user.quoteSummaryNextPayLead"),
      body: t("requests.user.quoteSummaryNextPay"),
    },
  ];

  return (
    <div className="space-y-4">
      <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-lg font-semibold leading-snug text-foreground">{title}</h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
          {attachments.length > 0 || intakeDetails.length > 0 ? (
            <ul className="space-y-1 pt-1">
              {intakeDetails.map((detail) => (
                <li
                  key={detail.label}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <PenLine className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">
                    {detail.label}
                    {": "}
                    <span className="text-foreground">{detail.value}</span>
                  </span>
                </li>
              ))}
              {attachments.map((a) => (
                <li
                  key={a.file.name}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{a.file.name}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold text-foreground">
            {t("requests.user.quoteSummaryNextTitle")}
          </h3>
          <ol>
            {steps.map((step, i) => (
              <li key={step.lead} className="relative flex gap-3 pb-5 last:pb-0">
                {/* The connector is what makes three facts read as one
                    sequence. Without it the dots are decoration on a list. */}
                {i < steps.length - 1 ? (
                  <span
                    aria-hidden
                    className="absolute bottom-1 left-3 top-7 w-px -translate-x-1/2 bg-border"
                  />
                ) : null}
                <StepDot>{i + 1}</StepDot>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium leading-6 text-foreground">{step.lead}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
          {/* Not a step, and it used to be tacked onto the last one as a second
              sentence that only restated it: if you pay on acceptance then
              sending is obviously free. Worse, the reassurance arrived at the
              END of the sequence, when the hesitation it answers is about the
              button directly below. It earns its place by saying something the
              steps do not — that asking commits you to nothing. */}
          <p className="flex items-start gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-px h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{t("requests.user.quoteSummaryNextFree")}</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function StepDot({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span
      className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary ring-4 ring-background"
      aria-hidden
    >
      {children}
    </span>
  );
}
